import React, { useEffect, useMemo, useRef, useState } from "react";


function previewUrl(src) {
  const value = String(src || "");
  if (
    !value ||
    value.includes("#t=") ||
    value.startsWith("blob:") ||
    value.startsWith("data:")
  ) return value;
  return `${value}#t=0.08`;
}

export function MediaImageCover({
  src,
  fallbackSrc = "",
  onFinalError,
  alt = "",
  className = "",
  eager = false,
  showStatus = false,
}) {
  const imageRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(!src);
  const [activeSrc, setActiveSrc] = useState(src || "");

  useEffect(() => {
    setReady(false);
    setFailed(!src);
    setActiveSrc(src || "");
  }, [src, fallbackSrc]);

  const revealDecodedImage = async (event) => {
    const image = event.currentTarget;
    try {
      await image.decode?.();
    } catch {
      // A completed load is still safe to reveal when decode() is unavailable.
    }
    if (imageRef.current === image) setReady(true);
  };

  return (
    <div
      className={`relative h-full w-full overflow-hidden bg-[#17284A] ${className}`}
      aria-busy={!ready && !failed}
    >
      <div
        aria-hidden="true"
        className={`absolute inset-0 flex items-center justify-center bg-[radial-gradient(circle_at_50%_24%,rgba(103,134,200,0.28)_0%,rgba(31,52,95,0.52)_42%,rgba(9,16,34,0.9)_100%)] transition-opacity duration-150 ${
          ready ? "opacity-0" : "opacity-100"
        }`}
      >
        {showStatus && !failed ? (
          <span className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white/80 motion-reduce:animate-none" />
        ) : showStatus && failed ? (
          <svg viewBox="0 0 24 24" className="h-8 w-8 text-white/45" fill="none">
            <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v11a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 17.5v-11Z" stroke="currentColor" strokeWidth="1.7" />
            <path d="m5 17 4.6-4.6 3.1 3.1 2.1-2.1L20 18.6M16.8 9.2h.01" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : null}
      </div>
      <img
        ref={imageRef}
        src={activeSrc}
        alt={alt}
        className={`pointer-events-none block h-full w-full select-none object-cover [filter:none] transition-opacity duration-150 ${
          ready ? "opacity-100" : "opacity-0"
        }`}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        draggable="false"
        onLoad={revealDecodedImage}
        onError={() => {
          if (fallbackSrc && activeSrc !== fallbackSrc) {
            setReady(false);
            setActiveSrc(fallbackSrc);
            return;
          }
          setFailed(true);
          onFinalError?.();
        }}
      />
    </div>
  );
}

function FirstFrameVideoCover({ src, className = "", eager = false, showStatus = false, primeOnMount = false }) {
  const videoRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const resolvedSrc = useMemo(() => previewUrl(src), [src]);
  const isLocalPreview = String(src || "").startsWith("blob:");

  useEffect(() => {
    setReady(false);
    setFailed(false);
  }, [resolvedSrc]);

  const requestFirstFrame = () => {
    const video = videoRef.current;
    if (!video || ready) return;
    try {
      const duration = Number(video.duration || 0);
      video.currentTime = duration > 0 ? Math.min(0.08, duration / 4) : 0.01;
    } catch {
      // The media-fragment URL remains the fallback for browsers that do not
      // allow seeking until more metadata is buffered.
    }
  };

  const warmFirstFrame = async () => {
    const video = videoRef.current;
    if (!video || ready) return;
    try {
      video.muted = true;
      const playAttempt = video.play();
      if (playAttempt && typeof playAttempt.then === "function") {
        await playAttempt;
      }
      video.pause();
      requestFirstFrame();
    } catch {
      requestFirstFrame();
    }
  };

  const markReady = () => setReady(true);

  return (
    <div
      className={`relative h-full w-full overflow-hidden bg-[#17284A] ${className}`}
      aria-busy={!ready && !failed}
    >
      <div
        aria-hidden="true"
        className={`absolute inset-0 flex items-center justify-center bg-[radial-gradient(circle_at_50%_22%,rgba(107,141,220,0.48)_0%,rgba(36,61,112,0.72)_40%,rgba(9,16,34,0.96)_100%)] transition-opacity duration-200 ${
          ready ? "opacity-0" : "opacity-100"
        }`}
      >
        {showStatus && !failed ? (
          <span className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white/80 motion-reduce:animate-none" />
        ) : showStatus && failed ? (
          <svg viewBox="0 0 24 24" className="h-8 w-8 text-white/45" fill="none">
            <path d="M5 4.5h10A2.5 2.5 0 0 1 17.5 7v10A2.5 2.5 0 0 1 15 19.5H5A2.5 2.5 0 0 1 2.5 17V7A2.5 2.5 0 0 1 5 4.5Z" stroke="currentColor" strokeWidth="1.7" />
            <path d="m17.5 9 4-2.2v10.4l-4-2.2V9Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
          </svg>
        ) : (
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/18 bg-black/20 text-white/88 shadow-[0_10px_24px_rgba(0,0,0,0.22)]">
            <svg viewBox="0 0 24 24" className="h-5 w-5 translate-x-[1px]" fill="currentColor">
              <path d="M8 5.8v12.4c0 .8.9 1.3 1.6.8l9-6.2a1 1 0 0 0 0-1.6l-9-6.2A1 1 0 0 0 8 5.8Z" />
            </svg>
          </span>
        )}
      </div>
      {!failed ? (
        <video
          ref={videoRef}
          src={resolvedSrc}
          className={`pointer-events-none block h-full w-full object-cover [filter:none] transition-opacity duration-200 ${
            ready ? "opacity-100" : "opacity-0"
          }`}
          muted
          playsInline
          autoPlay={primeOnMount}
          preload={eager || isLocalPreview ? "auto" : "metadata"}
          onLoadedMetadata={warmFirstFrame}
          onLoadedData={markReady}
          onCanPlay={markReady}
          onPlaying={() => {
            videoRef.current?.pause();
            markReady();
          }}
          onTimeUpdate={markReady}
          onSeeked={markReady}
          onError={() => setFailed(true)}
          tabIndex={-1}
          aria-hidden="true"
        />
      ) : null}
    </div>
  );
}


export default function MediaVideoCover({ src, posterSrc = "", className = "", eager = false, showStatus = false, remoteFirstFrame = false }) {
  const [posterFailed, setPosterFailed] = useState(false);
  useEffect(() => setPosterFailed(false), [posterSrc]);
  const isLocalPreview = /^(blob:|data:video\/)/i.test(String(src || ""));
  if (isLocalPreview) return <FirstFrameVideoCover src={src} className={className} eager={eager} showStatus={showStatus} />;
  if (posterSrc && !posterFailed) return <MediaImageCover src={posterSrc} className={className} eager={eager} showStatus={showStatus} onFinalError={() => setPosterFailed(true)} />;
  if (remoteFirstFrame && src) return <FirstFrameVideoCover src={src} className={className} eager={eager} showStatus={showStatus} primeOnMount />;
  return (
    <div className={`relative flex h-full w-full items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_50%_22%,rgba(107,141,220,0.48)_0%,rgba(36,61,112,0.72)_40%,rgba(9,16,34,0.96)_100%)] ${className}`}>
      <span aria-hidden="true" className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/18 bg-black/20 text-white/88">
        <svg viewBox="0 0 24 24" className="h-5 w-5 translate-x-[1px]" fill="currentColor"><path d="M8 5.8v12.4c0 .8.9 1.3 1.6.8l9-6.2a1 1 0 0 0 0-1.6l-9-6.2A1 1 0 0 0 8 5.8Z" /></svg>
      </span>
    </div>
  );
}
